import { useEffect, useRef, useState, useCallback } from 'react'

const prevent = (e) => {
  e.preventDefault()
  e.stopPropagation()
}

/**
 * @param {object} param0
 * @param {(files: File[]) => void} param0.onDrop
 * @param {string[]} [param0.acceptTypes]
 */
const useDragUpload = ({ onDrop, acceptTypes }) => {
  const ref = useRef(null)
  const [dragging, setDragging] = useState(false)
  const dragCounter = useRef(0)

  const handleDragIn = useCallback(
    (e) => {
      prevent(e)
      dragCounter.current++
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setDragging(true)
      }
    },
    [dragCounter],
  )

  const handleDragOut = useCallback(
    (e) => {
      prevent(e)
      dragCounter.current--
      if (dragCounter.current === 0) {
        setDragging(false)
      }
    },
    [dragCounter],
  )

  const handleDragStart = useCallback(
    (e) => {
      e.dataTransfer.clearData()
      dragCounter.current = 0
    },
    [dragCounter],
  )

  const handleDrop = useCallback(
    (e) => {
      prevent(e)
      setDragging(false)

      const { files } = e.dataTransfer
      if (files && files.length > 0) {
        const fileAry = [...files]
        const filtered = acceptTypes
          ? fileAry.filter((file) => acceptTypes.includes(file.type))
          : fileAry
        onDrop(filtered)
      }
    },
    [onDrop, acceptTypes],
  )

  useEffect(() => {
    const component = ref.current
    component.addEventListener('dragstart', handleDragStart)
    component.addEventListener('dragenter', handleDragIn)
    component.addEventListener('dragleave', handleDragOut)
    component.addEventListener('dragover', prevent)
    component.addEventListener('drop', handleDrop)

    return () => {
      component.removeEventListener('dragstart', handleDragStart)
      component.removeEventListener('dragenter', handleDragIn)
      component.removeEventListener('dragleave', handleDragOut)
      component.removeEventListener('dragover', prevent)
      component.removeEventListener('drop', handleDrop)
    }
  }, [ref, handleDragStart, handleDragIn, handleDragOut, handleDrop])

  return { dragging, ref }
}

export default useDragUpload
